import React, { useState, useMemo, useEffect } from "react";
import {
  Layout,
  Input,
  Space,
  Typography,
  Select,
  Button,
  Tag,
  Segmented,
  Form,
  notification,
  Tooltip,
} from "antd";
import {
  FolderOutlined,
  CloseCircleFilled,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CaseTable from "./CaseTable";
import CaseFormModal from "./CaseFormModal";
import PersonChip from "./PersonChip";
import { isCourseMember } from "../data";
import * as casesApi from "../api/cases";
import { useAppContext } from "../context/AppContext";

const { Content } = Layout;
const { Search } = Input;
const { Title, Text } = Typography;

const sortAlpha = (arr) => [...arr].sort((a, b) => a.localeCompare(b));

const CasesPage = ({ professorName, courseList, allCases, setAllCases }) => {
  const navigate = useNavigate();
  const { userRole, user } = useAppContext();
  const isProfessor = userRole === "Professeur";

  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [form] = Form.useForm();

  const editableCourseOptions = useMemo(
    () =>
      (courseList || [])
        .filter((c) => isCourseMember(c, user))
        .map((c) => ({ label: c.name, value: c.id })),
    [courseList, professorName]
  );

  const canAddCase = isProfessor && editableCourseOptions.length > 0;

  const handleOpenAddCase = () => {
    form.resetFields();
    setCaseModalOpen(true);
  };

  const handleSubmitCase = async (values) => {
    try {
      const { case: created, jobIds } = await casesApi.createCaseWithImages(values);
      setAllCases?.((prev) => [...prev, created]);
      setCaseModalOpen(false);
      if (jobIds.length) {
        notification.info({ message: "Cas ajouté — traitement des images en cours…" });
        casesApi.pollJobs(jobIds).then(async ({ failed }) => {
          const fresh = await casesApi.getCase(created.key);
          setAllCases?.((prev) => prev.map((c) => (c.key === created.key ? fresh : c)));
          if (failed.length) {
            notification.warning({ message: "Certaines images n'ont pas pu être traitées" });
          } else {
            notification.success({ message: "Images prêtes" });
          }
        });
      } else {
        notification.success({ message: "Cas ajouté" });
      }
    } catch (e) {
      notification.error({ message: "Échec de l'ajout", description: e.message });
    }
  };

  const [filters, setFilters] = useState({
    organ: null,
    region: null,
    pathology: null,
    course: null,
    owner: null,
  });
  const [searchText, setSearchText] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const courseLabelById = useMemo(() => {
    const m = {};
    courseList.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [courseList]);

  // Cascading facets: each filter's options come from the cases that match all
  // the OTHER active filters, so incompatible combinations never appear.
  const categories = useMemo(() => {
    const facetKeys = ["organ", "region", "pathology", "course", "owner"];
    const matchesExcept = (item, skip) =>
      facetKeys.every(
        (k) => k === skip || !filters[k] || item[k] === filters[k]
      ) && (!onlyMine || !isProfessor || item.owner === professorName);
    const uniq = (key) =>
      sortAlpha([
        ...new Set(
          allCases
            .filter((c) => matchesExcept(c, key))
            .map((c) => c[key])
            .filter(Boolean)
        ),
      ]);
    return {
      organs: uniq("organ"),
      regions: uniq("region"),
      pathologies: uniq("pathology"),
      owners: uniq("owner"),
      courses: sortAlpha(
        [...new Set(
          allCases
            .filter((c) => matchesExcept(c, "course"))
            .map((c) => c.course)
            .filter(Boolean)
        )].map(
          (id) => ({ id, name: courseLabelById[id] || id })
        ).map((c) => `${c.name}|${c.id}`)
      ).map((s) => {
        const [name, id] = s.split("|");
        return { id, name };
      }),
    };
  }, [allCases, courseLabelById, filters, onlyMine, isProfessor, professorName]);

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return allCases.filter((item) => {
      // Search spans every meaningful text field (pathology, organ, region,
      // clinical summary, owner, course name).
      const matchSearch = q
        ? [
            item.pathology,
            item.organ,
            item.region,
            item.clinicalSummary,
            item.owner,
            courseLabelById[item.course] || item.course,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        : true;

      const matchOrgan = filters.organ ? item.organ === filters.organ : true;
      const matchRegion = filters.region ? item.region === filters.region : true;
      const matchPathology = filters.pathology
        ? item.pathology === filters.pathology
        : true;
      const matchCourse = filters.course ? item.course === filters.course : true;
      const matchOwner = filters.owner ? item.owner === filters.owner : true;
      const matchMine = onlyMine && isProfessor ? item.owner === professorName : true;

      return (
        matchSearch &&
        matchOrgan &&
        matchRegion &&
        matchPathology &&
        matchCourse &&
        matchOwner &&
        matchMine
      );
    });
  }, [allCases, filters, searchText, onlyMine, isProfessor, professorName, courseLabelById]);

  const columns = [
    {
      title: "Pathologie",
      dataIndex: "pathology",
      key: "pathology",
      sorter: (a, b) => a.pathology.localeCompare(b.pathology),
    },
    {
      title: "Région",
      dataIndex: "region",
      key: "region",
      sorter: (a, b) => a.region.localeCompare(b.region),
    },
    {
      title: "Cours",
      dataIndex: "course",
      key: "course",
      sorter: (a, b) =>
        (courseLabelById[a.course] || "").localeCompare(courseLabelById[b.course] || ""),
      render: (courseId) => {
        const label = courseLabelById[courseId] || courseId;
        if (!courseLabelById[courseId]) return <Text type="secondary">{label}</Text>;
        return (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/cours/${courseId}`);
            }}
          >
            {label}
          </Button>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Propriétaire",
      dataIndex: "owner",
      key: "owner",
      sorter: (a, b) => a.owner.localeCompare(b.owner),
      render: (owner) => <PersonChip name={owner} />,
    },
  ];

  useEffect(() => {
    const calculatePageSize = () => {
      const totalHeight = window.innerHeight;
      const reserved = 300; // header + search + filters + pagination + padding
      const rowHeight = 65;
      const rows = Math.floor((totalHeight - reserved) / rowHeight);
      setPageSize(rows > 0 ? rows : 1);
    };
    calculatePageSize();
    window.addEventListener("resize", calculatePageSize);
    return () => window.removeEventListener("resize", calculatePageSize);
  }, []);

  const clearFilters = () =>
    setFilters({
      organ: null,
      region: null,
      pathology: null,
      course: null,
      owner: null,
    });

  const courseNameById = useMemo(() => {
    const m = {};
    categories.courses.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [categories.courses]);

  const activeFilterChips = [
    filters.organ && {
      key: "organ",
      label: `Organe : ${filters.organ}`,
      clear: () => setFilters((p) => ({ ...p, organ: null })),
    },
    filters.region && {
      key: "region",
      label: `Région : ${filters.region}`,
      clear: () => setFilters((p) => ({ ...p, region: null })),
    },
    filters.pathology && {
      key: "pathology",
      label: `Pathologie : ${filters.pathology}`,
      clear: () => setFilters((p) => ({ ...p, pathology: null })),
    },
    filters.course && {
      key: "course",
      label: `Cours : ${courseNameById[filters.course] || filters.course}`,
      clear: () => setFilters((p) => ({ ...p, course: null })),
    },
    filters.owner && {
      key: "owner",
      label: `Propriétaire : ${filters.owner}`,
      clear: () => setFilters((p) => ({ ...p, owner: null })),
    },
  ].filter(Boolean);

  const hasActiveFilters = activeFilterChips.length > 0;

  const filterFieldStyle = { minWidth: 180, flex: "1 1 180px" };

  return (
    <Content style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <FolderOutlined style={{ marginRight: 8 }} />
          Cas
        </Title>
        <Space>
          {isProfessor && (
            <Segmented
              options={[
                { label: "Tous", value: "all" },
                { label: "Mes cas", value: "mine" },
              ]}
              value={onlyMine ? "mine" : "all"}
              onChange={(v) => setOnlyMine(v === "mine")}
            />
          )}
          {isProfessor && (
            <Tooltip
              title={
                canAddCase
                  ? ""
                  : "Vous devez gérer au moins un cours pour ajouter un cas."
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenAddCase}
                disabled={!canAddCase}
              >
                Ajouter un cas
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>

      <Search
        placeholder="Rechercher (pathologie, organe, région, résumé, propriétaire, cours)…"
        size="large"
        style={{ width: "100%", marginBottom: 12 }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        allowClear
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Select
          placeholder="Organe"
          style={filterFieldStyle}
          value={filters.organ}
          onChange={(v) => setFilters((p) => ({ ...p, organ: v }))}
          allowClear
          showSearch
          optionFilterProp="label"
          options={categories.organs.map((o) => ({ label: o, value: o }))}
        />
        <Select
          placeholder="Région"
          style={filterFieldStyle}
          value={filters.region}
          onChange={(v) => setFilters((p) => ({ ...p, region: v }))}
          allowClear
          showSearch
          optionFilterProp="label"
          options={categories.regions.map((r) => ({ label: r, value: r }))}
        />
        <Select
          placeholder="Pathologie"
          style={filterFieldStyle}
          value={filters.pathology}
          onChange={(v) => setFilters((p) => ({ ...p, pathology: v }))}
          allowClear
          options={categories.pathologies.map((p) => ({ label: p, value: p }))}
        />
        <Select
          placeholder="Cours"
          style={filterFieldStyle}
          value={filters.course}
          onChange={(v) => setFilters((p) => ({ ...p, course: v }))}
          allowClear
          showSearch
          optionFilterProp="label"
          options={categories.courses.map((c) => ({ label: c.name, value: c.id }))}
        />
        <Select
          placeholder="Propriétaire"
          style={filterFieldStyle}
          value={filters.owner}
          onChange={(v) => setFilters((p) => ({ ...p, owner: v }))}
          allowClear
          showSearch
          options={categories.owners.map((o) => ({ label: o, value: o }))}
        />
      </div>

      {hasActiveFilters && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            Filtres actifs :
          </Text>
          {activeFilterChips.map((chip) => (
            <Tag
              key={chip.key}
              closable
              closeIcon={<CloseCircleFilled />}
              onClose={chip.clear}
              style={{ marginRight: 0 }}
            >
              {chip.label}
            </Tag>
          ))}
          <Button type="link" size="small" onClick={clearFilters}>
            Tout effacer
          </Button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          minHeight: 24,
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {filteredData.length} cas
          {hasActiveFilters || searchText || (onlyMine && isProfessor)
            ? " correspondants"
            : ""}
        </Text>
      </div>

      <CaseTable
        dataSource={filteredData}
        columns={columns}
        pagination={{ pageSize }}
        locale={{
          emptyText: (
            <div style={{ padding: "32px 16px" }}>
              <Title level={5} style={{ marginBottom: 4 }}>
                Aucun cas
              </Title>
              <Text type="secondary">
                {hasActiveFilters || searchText
                  ? "Aucun cas ne correspond à vos filtres."
                  : "Aucun cas n'est encore disponible."}
              </Text>
              {(hasActiveFilters || searchText) && (
                <div style={{ marginTop: 16 }}>
                  <Button
                    onClick={() => {
                      clearFilters();
                      setSearchText("");
                    }}
                  >
                    Effacer les filtres
                  </Button>
                </div>
              )}
            </div>
          ),
        }}
      />

      {isProfessor && (
        <CaseFormModal
          open={caseModalOpen}
          onCancel={() => setCaseModalOpen(false)}
          onFinish={handleSubmitCase}
          form={form}
          isEditing={false}
          courseOptions={editableCourseOptions}
        />
      )}
    </Content>
  );
};

export default CasesPage;
